import { useQueries, useQuery } from "@tanstack/react-query";
import { getPlayer, getPlayerLineups, getPlayerStats, ApiError } from "../api";
import {
  LimitedPlayerDetails,
  PlayerDetails,
  PlayerLineupsResponse,
  PlayerStats,
} from "../api/types";

const ONE_HOUR_MS = 60 * 60 * 1000;

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const playerKeys = {
  all: ["player"] as const,
  current: () => [...playerKeys.all, "current"] as const,
  detail: (playerId: string, draftId?: string) =>
    [...playerKeys.all, playerId, draftId] as const,
  stats: (
    playerId: string,
    draftId?: string,
    startDate?: string,
    endDate?: string,
  ) =>
    [
      ...playerKeys.all,
      "stats",
      playerId,
      draftId,
      startDate,
      endDate,
    ] as const,
  lineups: (playerId: string, draftId?: string, date?: string) =>
    [...playerKeys.all, "lineups", playerId, draftId, date] as const,
};

export function useGetPlayer(playerId: string | undefined, draftId?: string) {
  return useQuery<PlayerDetails | LimitedPlayerDetails, ApiError>({
    queryKey: playerKeys.detail(playerId ?? "", draftId),
    queryFn: () => getPlayer({ playerId, draftId }),
    enabled: !!playerId,
    staleTime: FIVE_MINUTES_MS,
  });
}

export function useGetPlayers(playerIds: string[], draftId?: string) {
  return useQueries({
    queries: playerIds.map((playerId) => ({
      queryKey: playerKeys.detail(playerId, draftId),
      queryFn: () => getPlayer({ playerId, draftId }),
      staleTime: FIVE_MINUTES_MS,
      enabled: playerIds.length > 0,
    })),
  });
}

export function useCurrentPlayer() {
  return useQuery<PlayerDetails, ApiError>({
    queryKey: playerKeys.current(),
    queryFn: () => getPlayer() as Promise<PlayerDetails>,
    staleTime: FIVE_MINUTES_MS,
  });
}

export function usePlayerStats(
  playerId: string,
  params?: { draftId?: string; startDate?: string; endDate?: string },
) {
  return useQuery<PlayerStats, ApiError>({
    queryKey: playerKeys.stats(
      playerId,
      params?.draftId,
      params?.startDate,
      params?.endDate,
    ),
    queryFn: () => getPlayerStats(playerId, params ?? {}),
    staleTime: ONE_HOUR_MS,
  });
}

export function usePlayerLineups(
  playerId: string,
  params: { draftId?: string; date?: string },
) {
  return useQuery<PlayerLineupsResponse, ApiError>({
    queryKey: playerKeys.lineups(playerId, params.draftId, params.date),
    queryFn: () =>
      getPlayerLineups(playerId, params as { draftId: string; date?: string }),
    enabled: !!params.draftId,
  });
}
